import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ShieldAlert, ShieldCheck, ShieldX } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { supabase } from "@/lib/supabase";

type NoShowStatus = "open" | "overridden" | "upheld";

interface NoShowRow {
  id: string;
  user_id: string;
  match_id: string | null;
  flagged_by: string | null;
  notes: string | null;
  status: NoShowStatus;
  created_at: string;
  user_name: string | null;
  flagged_by_name: string | null;
}

/**
 * Admin → No-shows. Captain-authored no-show reports that feed the
 * automatic 2-flags-in-90-days ban rule. Override (clear) to
 * effectively cancel; Uphold to keep on record.
 */
export function AdminNoShows() {
  const [rows, setRows] = useState<NoShowRow[] | null>(null);
  const [status, setStatus] = useState<NoShowStatus | "all">("open");
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    let q = supabase
      .from("no_show_flags")
      .select(
        "id, user_id, match_id, flagged_by, notes, status, created_at, user:profiles!no_show_flags_user_profile_fkey(display_name), flagger:profiles!no_show_flags_flagged_profile_fkey(display_name)"
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (status !== "all") q = q.eq("status", status);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setRows([]);
      return;
    }
    setRows(
      ((data ?? []) as unknown as Array<{
        id: string;
        user_id: string;
        match_id: string | null;
        flagged_by: string | null;
        notes: string | null;
        status: NoShowStatus;
        created_at: string;
        user: { display_name: string | null } | null;
        flagger: { display_name: string | null } | null;
      }>).map((r) => ({
        id: r.id,
        user_id: r.user_id,
        match_id: r.match_id,
        flagged_by: r.flagged_by,
        notes: r.notes,
        status: r.status,
        created_at: r.created_at,
        user_name: r.user?.display_name ?? null,
        flagged_by_name: r.flagger?.display_name ?? null,
      }))
    );
  }

  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function resolve(id: string, newStatus: "overridden" | "upheld") {
    setBusyId(id);
    const { error } = await supabase
      .from("no_show_flags")
      .update({ status: newStatus, resolved_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      setBusyId(null);
      return toast.error(error.message);
    }
    await supabase.rpc("log_audit", {
      action: `no_show_flag_${newStatus}`,
      entity_type: "no_show_flag",
      entity_id: id,
      details: {},
    });
    toast.success(newStatus === "overridden" ? "Overridden" : "Upheld");
    setBusyId(null);
    load();
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · No-shows
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">No-shows</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Flags captains raise after a match when a player didn't show.
          These feed the 2-flags-in-90-days automatic ban rule.
        </p>
      </header>

      <StatusTabs value={status} onChange={setStatus} />

      {rows === null ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-border bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Player</th>
                <th className="px-4 py-2 text-left">Flagged by</th>
                <th className="px-4 py-2 text-left">Notes</th>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-b-0 align-top">
                  <td className="px-4 py-3 font-medium">
                    {r.user_name ?? <span className="text-muted-foreground">Unknown</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {r.flagged_by_name ?? <span className="text-muted-foreground">System</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.notes ? (
                      <span className="line-clamp-3">{r.notes}</span>
                    ) : (
                      <span className="opacity-60">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {r.status !== "open" ? (
                      <StatusPill status={r.status} />
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve(r.id, "overridden")}
                          disabled={busyId === r.id}
                        >
                          {busyId === r.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ShieldX className="h-3 w-3" />
                          )}
                          Override
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => resolve(r.id, "upheld")}
                          disabled={busyId === r.id}
                        >
                          <ShieldCheck className="h-3 w-3" />
                          Uphold
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusTabs({
  value,
  onChange,
}: {
  value: NoShowStatus | "all";
  onChange: (v: NoShowStatus | "all") => void;
}) {
  const options: Array<{ label: string; value: NoShowStatus | "all" }> = [
    { label: "Open", value: "open" },
    { label: "Upheld", value: "upheld" },
    { label: "Overridden", value: "overridden" },
    { label: "All", value: "all" },
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
            (value === o.value
              ? "bg-volt text-primary-foreground"
              : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: NoShowStatus }) {
  if (status === "overridden") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
        <ShieldX className="h-2.5 w-2.5" /> Overridden
      </span>
    );
  }
  if (status === "upheld") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
        <CheckCircle2 className="h-2.5 w-2.5" /> Upheld
      </span>
    );
  }
  return null;
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-volt-subtle text-volt">
        <ShieldAlert className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">No no-shows</p>
      <p className="mt-1 text-xs text-muted-foreground">
        When captains flag players for not turning up, entries land
        here.
      </p>
    </div>
  );
}
